/**
 * View communication log — same behavior as Angular CommunicationLogAddComponent with `isView: true`
 * (dialog title "Log Communication", read-only fields, optional Update Comment for the log author).
 */
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X, Loader2, Calendar } from "lucide-react"
import modalStyles from "@/features/application/styles/modals.module.css"
import commLogStyles from "@/features/company/styles/communicationLogModal.module.css"
import { formatCommunicationLogDateTime } from "@/lib/utils/communicationLogDates"
import apiClient from "@/lib/api/apiClient"
import { showError, showSuccess } from "@/lib/utils/notifications"
import { useUserRole } from "@/hooks/useUserRole"
import {
  COMMUNICATION_TYPES,
  COMMUNICATION_CATEGORY,
  COMMUNICATION_DIRECTION,
} from "@/features/company/utils/communicationLogLabels"
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock"
import type { CommunicationLogViewModalProps } from '../types'

function ReadOnlyField({
  label,
  required,
  children,
  className = "",
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`${commLogStyles.field} ${className}`}>
      <label className={commLogStyles.fieldLabel}>
        {label}
        {required ? <span className={commLogStyles.required}> *</span> : null}
      </label>
      <div className={commLogStyles.readOnlyValue}>{children}</div>
    </div>
  )
}

export function CommunicationLogViewModal({
  isOpen,
  onClose,
  log,
  onCommentUpdated,
}: CommunicationLogViewModalProps) {
  const { user } = useUserRole()
  const [comment, setComment] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (log) {
      setComment(log.comment ?? "")
    }
  }, [log])

  useBodyScrollLock(isOpen)

  if (!isOpen || !log) return null

  const allowCommentUpdate =
    user?.user_id != null &&
    log.fk_user_id != null &&
    Number(user.user_id) === Number(log.fk_user_id)

  const typeLabel = COMMUNICATION_TYPES[log.type ?? ""] || log.type || "—"
  const categoryLabel = COMMUNICATION_CATEGORY[log.category ?? ""] || log.category || "—"

  const logDt = formatCommunicationLogDateTime(log.log_datetime)

  const handleUpdateComment = async () => {
    setSaving(true)
    try {
      await apiClient.post(`/api/communication_log_update/${log.log_id}`, { comment })
      showSuccess("Communication Log Comment Updated Successfully")
      onCommentUpdated?.()
      onClose()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
          ?.message ??
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
          ?.error ??
        "Failed to update comment"
      showError(typeof msg === "string" ? msg : "Failed to update comment")
    } finally {
      setSaving(false)
    }
  }

  const modalContent = (
    <div
      data-testid="company-comm-log-view-overlay"
      className={`${modalStyles.modalOverlay} !z-[10002]`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="comm-log-view-title"
      onClick={onClose}
    >
      <div
        data-testid="company-comm-log-view-panel"
        className={`${modalStyles.modalContent} ${modalStyles.modalContent800} ${modalStyles.modalContentStacked}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={commLogStyles.header}>
          <div className={commLogStyles.headerLead}>
            <div className="min-w-0">
              <h3 id="comm-log-view-title" className={commLogStyles.title}>
                Log Communication
              </h3>
            </div>
          </div>
          <button
            data-testid="company-comm-log-view-close-button"
            type="button"
            onClick={onClose}
            className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={commLogStyles.body}>
          {log.linked_contact_users ? (
            <p className="mb-4 text-sm text-slate-600">{log.linked_contact_users}</p>
          ) : null}
          <div className="space-y-4">
            {log.type === "OTHER" ? (
              <div className={commLogStyles.fieldGrid}>
                <ReadOnlyField label="Type" required>
                  {typeLabel}
                </ReadOnlyField>
                <ReadOnlyField label="Other Type Reason" required>
                  {log.type_other?.trim() ? log.type_other : "—"}
                </ReadOnlyField>
              </div>
            ) : (
              <ReadOnlyField label="Type" required>
                {typeLabel}
              </ReadOnlyField>
            )}

            {log.category === "OTHER" ? (
              <div className={commLogStyles.fieldGrid}>
                <ReadOnlyField label="Category" required>
                  {categoryLabel}
                </ReadOnlyField>
                <ReadOnlyField label="Other Category Reason" required>
                  {log.category_other?.trim() ? log.category_other : "—"}
                </ReadOnlyField>
              </div>
            ) : (
              <ReadOnlyField label="Category" required>
                {categoryLabel}
              </ReadOnlyField>
            )}

            <div className={commLogStyles.fieldGrid}>
              <div className={commLogStyles.field}>
                <label className={commLogStyles.fieldLabel}>Date and Time</label>
                <div className={`${commLogStyles.readOnlyValue} justify-between gap-2`}>
                  <span className={`min-w-0 flex-1 truncate ${logDt ? "" : commLogStyles.readOnlyValueMuted}`}>
                    {logDt || "mm/dd/yyyy, --:-- --"}
                  </span>
                  <Calendar className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                </div>
              </div>
              <ReadOnlyField label="Direction" required>
                {COMMUNICATION_DIRECTION[log.direction ?? ""] || log.direction || "—"}
              </ReadOnlyField>
            </div>

            <div className={commLogStyles.field}>
              <label htmlFor="comm-log-comment" className={commLogStyles.fieldLabel}>
                Comments<span className={commLogStyles.required}> *</span>
              </label>
              <textarea
                data-testid="company-comm-log-view-comment-textarea"
                id="comm-log-comment"
                rows={6}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={!allowCommentUpdate}
                className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/30 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-700"
              />
            </div>
          </div>
        </div>

        <div className={commLogStyles.footer}>
          <div className={`${commLogStyles.footerActions} ${modalStyles.modalFooterActionGroup}`}>
            {allowCommentUpdate ? (
              <button
                data-testid="company-comm-log-view-update-comment-button"
                type="button"
                onClick={handleUpdateComment}
                disabled={saving}
                className={modalStyles.modalActionPrimary}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Update Comment
              </button>
            ) : null}
            <button
              data-testid="company-comm-log-view-cancel-button"
              type="button"
              onClick={onClose}
              className={modalStyles.modalActionSecondary}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
